import type { GetRequestsResponse } from "@nlc/shared-types";
import type { SortDescriptor } from "react-stately";

import { useQuery } from "@tanstack/react-query";

import { requestListKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

import type { RequestFilterType } from "../../types/filters";

import { getRequestTypes } from "../../utils/requestUtils";
import { mapSortColumnToBackend } from "../../utils/sortingUtils";

interface UseArchivedRequestsParams {
  enabled?: boolean;
  filter?: RequestFilterType;
  limit?: number;
  page?: number;
  search?: string;
  sortColumn?: string;
  sortDescriptor?: SortDescriptor;
  sortDirection?: "asc" | "desc";
}

export function useArchivedRequests(params: UseArchivedRequestsParams = {}) {
  const { data: userData } = useUser();
  const projectId = userData?.currentProjectId;

  return useQuery({
    enabled: !!userData?.user && (params.enabled ?? true),
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      const queryParams: Record<string, number | string | string[]> = {
        direction: params.sortDescriptor
          ? params.sortDescriptor.direction === "descending"
            ? "desc"
            : "asc"
          : params.sortDirection || "desc",
        fields: [
          "id",
          "name",
          "request_type",
          "status",
          "created_by",
          "brand",
          "created_at",
          "design_type",
        ],
        limit: params.limit || 10,
        page: params.page || 1,
        skipCount: "false",
        sortBy: params.sortDescriptor
          ? mapSortColumnToBackend(String(params.sortDescriptor.column))
          : mapSortColumnToBackend(params.sortColumn || "createdAt"),
        tab: "archived",
      };

      queryParams.skipCount = "false";

      if (projectId) {
        queryParams.projectId = projectId;
      }

      if (params.filter && params.filter !== "all") {
        const filterTypes = getRequestTypes(params.filter);

        queryParams.category = filterTypes;
      }

      if (params.search) {
        queryParams.search = params.search;
      }

      return await apiClient
        .url("/requests/list")
        .query(queryParams)
        .get()
        .json<GetRequestsResponse>();
    },
    queryKey: [
      ...requestListKeys.all,
      "archived",
      projectId,
      params.filter,
      params.limit,
      params.page,
      params.search,
      params.sortDescriptor?.column || params.sortColumn,
      params.sortDescriptor?.direction || params.sortDirection,
    ],
    refetchInterval: (data) =>
      data && document.visibilityState === "visible" ? 45 * 1000 : false,
    // Shorter stale time while sorting so results stay fresh
    staleTime:
      params.sortDescriptor || params.sortColumn
        ? 1 * 60 * 1000
        : 3 * 60 * 1000,
  });
}
